import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpFromBracket,
  faCheck,
  faCircleInfo,
  faFileArrowDown,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import Tooltip from '@shared/components/ui/tooltip/tooltip';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { useToast } from '@shared/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import {
  AmazonFbmImportApiAdapter,
  type AmazonFbmPreviewResponse,
  type AmazonFbmImportResponse,
  type AmazonFbmPreviewOrder,
  type AmazonFbmPreviewLine,
  type AmazonFbmPreviewRootErrorItem,
} from '../../adapters/AmazonFbmImportApiAdapter';
import { cn } from '@shared/utils/utils';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';

type Step = 'file' | 'preview' | 'result';

interface AmazonFbmImportModalProps {
  shopId: number;
  onSuccess?: () => void;
}

export interface AmazonFbmImportModalRef {
  open: () => void;
  close: () => void;
}

const adapter = new AmazonFbmImportApiAdapter(createHttpClient(apiClientWH));

function formatCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

/** Vinculado si hay producto o pack del sistema */
function isLineLinked(line: AmazonFbmPreviewLine): boolean {
  return !!(line.product || line.pack);
}

/** Nombre del producto/pack vinculado × cantidad, o SKU × cantidad si no hay vínculo */
function getLineDisplayText(line: AmazonFbmPreviewLine): string {
  if (line.product) {
    return `${line.product.name} × ${line.qty}`;
  }
  if (line.pack) {
    const label = line.pack.title ?? (line.pack.id != null ? `Pack #${line.pack.id}` : null);
    if (label) {
      return `${label} × ${line.qty}`;
    }
  }
  const sku = line.external_variant_id ?? '—';
  return `${sku} × ${line.qty}`;
}

function formatRootError(e: AmazonFbmPreviewRootErrorItem): string {
  if (typeof e === 'string') return e;
  return `Línea ${e.line}: ${e.message}`;
}

export const AmazonFbmImportModal = forwardRef<AmazonFbmImportModalRef, AmazonFbmImportModalProps>(
  ({ shopId, onSuccess }, ref) => {
    const { t } = useTranslation('shopsModule');
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const modalRef = useRef<ModalCenterRef>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [step, setStep] = useState<Step>('file');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [previewData, setPreviewData] = useState<AmazonFbmPreviewResponse | null>(null);
    const [importResult, setImportResult] = useState<AmazonFbmImportResponse | null>(null);

    const resetState = () => {
      setStep('file');
      setSelectedFile(null);
      setDragActive(false);
      setError(null);
      setPreviewData(null);
      setImportResult(null);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    };

    useImperativeHandle(ref, () => ({
      open: () => {
        resetState();
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }));

    const handleFileSelect = (file?: File) => {
      if (!file) return;
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      if (ext !== '.tsv' && ext !== '.txt') {
        setError(t('modals.amazonFbmImport.invalidFile'));
        return;
      }
      setSelectedFile(file);
      setError(null);
      setPreviewData(null);
      setImportResult(null);
      setStep('file');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      handleFileSelect(file);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      handleFileSelect(e.dataTransfer.files?.[0]);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
    };

    const handlePreview = async () => {
      if (!selectedFile) {
        setError(t('modals.amazonFbmImport.noFile'));
        return;
      }
      setError(null);
      setIsLoading(true);
      try {
        const data = await adapter.preview(selectedFile, shopId);
        setPreviewData(data);
        setStep('preview');
      } catch (err: any) {
        const message = err.response?.data?.message || err.message || t('toasts.error.unexpected');
        setError(message);
        toast({
          title: t('modals.settings.toast.error.title'),
          description: message,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    const handleConfirmImport = async () => {
      if (!selectedFile) return;
      setError(null);
      setIsLoading(true);
      try {
        const result = await adapter.import(selectedFile, shopId);
        setImportResult(result);
        setStep('result');
        toast({
          title: t('modals.settings.toast.success.title'),
          description: t('modals.importOrder.toast.success.description'),
          variant: 'default',
        });
        await queryClient.invalidateQueries({ queryKey: ['shops'], exact: false });
        await queryClient.invalidateQueries({ queryKey: ['orders'], exact: false });
        onSuccess?.();
      } catch (err: any) {
        const message = err.response?.data?.message || err.message || t('toasts.error.unexpected');
        setError(message);
        toast({
          title: t('modals.settings.toast.error.title'),
          description: message,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    const handleClose = () => {
      if (!isLoading) {
        modalRef.current?.closeModal();
      }
    };

    const dropzoneClasses = [
      'flex flex-col items-center justify-center gap-2 px-6 py-6 rounded-lg border border-dashed transition-colors',
      dragActive ? 'border-primary bg-card-bg' : 'border-title bg-background',
      isLoading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
    ].join(' ');

    const renderStepContent = () => {
      if (step === 'file') {
        return (
          <>
            <p className="text-sm text-complementary-text">{t('modals.amazonFbmImport.description')}</p>
            <div
              className={dropzoneClasses}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !isLoading && inputRef.current?.click()}
            >
              <input ref={inputRef} type="file" accept=".tsv,.txt" className="hidden" onChange={handleInputChange} />
              <FontAwesomeIcon
                icon={faArrowUpFromBracket}
                className={selectedFile ? '!h-10 !w-10 text-primary' : 'h-9 w-9 text-icons'}
              />
              <p className="text-sm text-complementary-text text-center">
                {selectedFile ? (
                  <span className="text-title font-medium">{selectedFile.name}</span>
                ) : (
                  t('modals.amazonFbmImport.dropzonePrompt')
                )}
              </p>
              <p className="text-xs text-complementary-text">{t('modals.amazonFbmImport.formatHint')}</p>
            </div>
            {error && <p className="text-sm text-error">{error}</p>}
            <div className="flex justify-end gap-3">
              <Button variant="outline" size="lg" onClick={handleClose} disabled={isLoading}>
                {t('modals.settings.buttons.cancel')}
              </Button>
              <Button variant="primary" size="lg" onClick={handlePreview} disabled={!selectedFile || isLoading}>
                {isLoading ? t('common.loading') : t('modals.amazonFbmImport.preview')}
              </Button>
            </div>
          </>
        );
      }

      if (step === 'preview' && previewData) {
        return (
          <>
            <div className="rounded-lg border border-card-border bg-card-bg p-4 mb-4 space-y-2">
              <h4 className="text-sm font-semibold mb-3">{t('modals.amazonFbmImport.previewResults.summary')}</h4>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <span>
                  {t('modals.amazonFbmImport.previewResults.total')}: {previewData.total_orders}
                </span>
                <span>
                  {t('modals.amazonFbmImport.previewResults.totalLines')}: {previewData.total_lines}
                </span>
                <span>
                  {t('modals.amazonFbmImport.previewResults.valid')}: {previewData.valid_orders}
                </span>
                <span>
                  {t('modals.amazonFbmImport.previewResults.withErrors')}: {previewData.orders_with_errors}
                </span>
              </div>
              {previewData.errors?.length > 0 && (
                <ul className="text-sm text-error list-disc list-inside mt-2">
                  {previewData.errors.map((e, i) => (
                    <li key={i}>{formatRootError(e)}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-1">{t('modals.amazonFbmImport.previewResults.ordersTable')}</h4>
              <p className="text-xs text-complementary-text mb-2">
                {t('modals.amazonFbmImport.previewResults.sampleNote')}
              </p>
              {/*
              Un solo contenedor de scroll (sin el wrapper overflow-auto de <Table /> de shadcn)
              para que la barra horizontal quede siempre al pie del área visible, no al final del contenido.
            */}
              <div
                className={cn(
                  'max-h-[min(480px,calc(100vh-16rem))] overflow-y-auto overflow-x-scroll rounded-lg border border-card-border',
                  'scrollbar-gutter-stable',
                )}
              >
                <table className="w-full min-w-max caption-bottom text-sm">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky left-0 z-10 min-w-[130px] bg-card-bg">
                        {t('modals.amazonFbmImport.table.orderId')}
                      </TableHead>
                      <TableHead className="min-w-[120px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.shopName')}
                      </TableHead>
                      <TableHead className="min-w-[120px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.buyerName')}
                      </TableHead>
                      <TableHead className="min-w-[160px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.buyerEmail')}
                      </TableHead>
                      <TableHead className="min-w-[100px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.buyerPhone')}
                      </TableHead>
                      <TableHead className="min-w-[140px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.address1')}
                      </TableHead>
                      <TableHead className="min-w-[120px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.address2')}
                      </TableHead>
                      <TableHead className="min-w-[100px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.city')}
                      </TableHead>
                      <TableHead className="min-w-[100px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.province')}
                      </TableHead>
                      <TableHead className="min-w-[80px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.zip')}
                      </TableHead>
                      <TableHead className="min-w-[70px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.country')}
                      </TableHead>
                      <TableHead className="min-w-[260px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.products')}
                      </TableHead>
                      <TableHead className="min-w-[200px] whitespace-normal">
                        {t('modals.amazonFbmImport.table.errors')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.preview_orders.map((order: AmazonFbmPreviewOrder, idx: number) => {
                      const orderRef = order.ref ?? order.external_id ?? '—';
                      const key = `${orderRef}-${idx}`;
                      const addr = order.customer?.address;
                      return (
                        <TableRow key={key}>
                          <TableCell className="sticky left-0 z-[1] min-w-[130px] bg-card-bg font-medium align-top">
                            <span className="break-all">{orderRef}</span>
                          </TableCell>
                          <TableCell className="align-top text-xs whitespace-normal break-words max-w-[200px]">
                            {formatCell(order.shop?.name)}
                          </TableCell>
                          <TableCell className="align-top text-xs whitespace-normal break-words max-w-[200px]">
                            {formatCell(order.customer?.name)}
                          </TableCell>
                          <TableCell className="align-top text-xs whitespace-normal break-all max-w-[220px]">
                            {formatCell(order.customer?.email)}
                          </TableCell>
                          <TableCell className="align-top text-xs whitespace-normal break-words">
                            {formatCell(order.customer?.phone)}
                          </TableCell>
                          <TableCell className="align-top text-xs whitespace-normal break-words max-w-[200px]">
                            {formatCell(addr?.address1)}
                          </TableCell>
                          <TableCell className="align-top text-xs whitespace-normal break-words max-w-[180px]">
                            {formatCell(addr?.address2)}
                          </TableCell>
                          <TableCell className="align-top text-xs whitespace-normal break-words">
                            {formatCell(addr?.city)}
                          </TableCell>
                          <TableCell className="align-top text-xs whitespace-normal break-words">
                            {formatCell(addr?.province)}
                          </TableCell>
                          <TableCell className="align-top text-xs whitespace-normal">{formatCell(addr?.zip)}</TableCell>
                          <TableCell className="align-top text-xs whitespace-normal">
                            {formatCell(addr?.country_code)}
                          </TableCell>
                          <TableCell className="align-top min-w-[260px]">
                            {(order.lines ?? []).length === 0 ? (
                              <span className="text-xs text-complementary-text">—</span>
                            ) : (
                              <div className="flex flex-col gap-1.5">
                                {(order.lines ?? []).map((line, i) => (
                                  <div
                                    key={i}
                                    className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/30 px-2 py-1.5"
                                  >
                                    <Tooltip
                                      side="top"
                                      align="start"
                                      className="max-w-[280px]"
                                      trigger={
                                        <span
                                          className={cn(
                                            'inline-flex shrink-0 cursor-help items-center justify-center rounded-sm mt-0.5',
                                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                          )}
                                          tabIndex={0}
                                          role="img"
                                          aria-label={
                                            isLineLinked(line)
                                              ? t('modals.amazonFbmImport.lineLinkedAria')
                                              : t('modals.amazonFbmImport.lineUnlinkedAria')
                                          }
                                        >
                                          <FontAwesomeIcon
                                            icon={isLineLinked(line) ? faCheck : faXmark}
                                            className={cn(
                                              'h-3.5 w-3.5',
                                              isLineLinked(line) ? 'text-success' : 'text-error',
                                            )}
                                            aria-hidden
                                          />
                                        </span>
                                      }
                                      content={
                                        <span className="text-xs leading-snug">
                                          {isLineLinked(line)
                                            ? t('modals.amazonFbmImport.lineLinkedTooltip')
                                            : t('modals.amazonFbmImport.lineUnlinkedTooltip')}
                                        </span>
                                      }
                                    />
                                    <span className="text-xs leading-snug break-words text-foreground">
                                      {getLineDisplayText(line)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="align-top text-xs whitespace-normal break-words max-w-[280px]">
                            {order.errors?.length ? (
                              <span className="text-error">{order.errors.join(' · ')}</span>
                            ) : (
                              <span className="text-complementary-text">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </table>
              </div>
            </div>

            <Alert className="border-border bg-muted/40 border" role="alert">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <div className="space-y-1 min-w-0">
                  <AlertTitle className="text-sm font-semibold text-foreground">
                    {t('modals.amazonFbmImport.importDisclaimer.title')}
                  </AlertTitle>
                  <AlertDescription className="text-sm text-complementary-text leading-snug">
                    {t('modals.amazonFbmImport.importDisclaimer.description')}
                  </AlertDescription>
                </div>
              </div>
            </Alert>

            {error && <p className="text-sm text-error mb-4">{error}</p>}
            <div className="flex justify-end gap-3">
              <Button variant="outline" size="lg" onClick={handleClose} disabled={isLoading}>
                {t('modals.settings.buttons.cancel')}
              </Button>
              <Button
                variant="primary"
                size="lg"
                onClick={handleConfirmImport}
                disabled={isLoading || previewData.valid_orders === 0}
              >
                {isLoading ? t('common.loading') : t('modals.amazonFbmImport.confirm')}
              </Button>
            </div>
          </>
        );
      }

      if (step === 'result' && importResult) {
        return (
          <>
            <div className="rounded-lg border border-card-border bg-card-bg p-4 space-y-2">
              <h4 className="text-sm font-semibold">{t('modals.amazonFbmImport.importResults.title')}</h4>
              <div className="flex flex-wrap gap-4 text-sm">
                <span>
                  {t('modals.amazonFbmImport.importResults.success')}: {importResult.success_count}
                </span>
                <span>
                  {t('modals.amazonFbmImport.importResults.errors')}: {importResult.error_count}
                </span>
              </div>
              {importResult.error_file && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => window.open(importResult.error_file as string, '_blank')}
                  icon={faFileArrowDown}
                  iconPosition="start"
                >
                  {t('modals.amazonFbmImport.downloadErrors')}
                </Button>
              )}
            </div>
            <div className="flex justify-end">
              <Button variant="primary" size="lg" onClick={handleClose}>
                {t('modals.amazonFbmImport.close')}
              </Button>
            </div>
          </>
        );
      }

      return null;
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('modals.amazonFbmImport.title')}
        width="xxl"
        type="default"
        className="bg-card-bg"
      >
        <div className="space-y-6">{renderStepContent()}</div>
      </ModalCenter>
    );
  },
);

AmazonFbmImportModal.displayName = 'AmazonFbmImportModal';
